import { apiClient } from './client';

export interface EventTaskAssignment {
  id: string;
  memberId: string;
  roleId: string | null;
  member: { id: string; firstName: string; lastName: string; userId?: string | null };
  role: { id: string; name: string } | null;
}

export interface EventTaskDetail {
  id: string;
  title: string;
  description: string;
  status: 'ToDo' | 'InProgress' | 'Done' | 'Cancelled';
  priority: 'High' | 'Medium' | 'Low';
  category: 'Weekend' | 'Planning' | 'General' | 'Other';
  dueDate: string;
  completedAt: string | null;
  fulfillmentComments: string | null;
  event: { id: string; name: string; date?: string } | null;
  subcommittee: { id: string; name: string };
  assignee: { id: string; firstName: string; lastName: string } | null;
  createdBy: { id: string; firstName: string; lastName: string };
  assignees: EventTaskAssignment[];
  createdAt: string;
  updatedAt: string;
}

export interface EventTask {
  id: string;
  title: string;
  description: string;
  status: 'ToDo' | 'InProgress' | 'Done' | 'Cancelled';
  priority: 'High' | 'Medium' | 'Low';
  category: 'Weekend' | 'Planning' | 'General' | 'Other';
  dueDate: string;
  completedAt: string | null;
  event: { id: string; name: string } | null;
  subcommittee: { id: string; name: string };
  assignee: { id: string; firstName: string; lastName: string; userId?: string | null };
  assignees?: EventTaskAssignment[];
  createdBy: { id: string; firstName: string; lastName: string };
  createdAt: string;
  updatedAt: string;
}

export interface MyEventTasksFilter {
  subcommitteeId?: string;
  status?: string;
  category?: string;
  eventId?: string;
}

export interface CreateEventTaskPayload {
  title: string;
  description: string;
  category: string;
  eventId?: string;
  subcommitteeId: string;
  assigneeId: string;
  dueDate: string;
  priority?: string;
}

export interface UpdateEventTaskPayload {
  title?: string;
  description?: string;
  status?: string;
  priority?: string;
  category?: string;
  eventId?: string;
  dueDate?: string;
  assigneeId?: string;
  fulfillmentComments?: string;
}

export function getTask(id: string): Promise<EventTaskDetail> {
  return apiClient<EventTaskDetail>(`/event-tasks/${id}`);
}

export function getMyTasks(filters?: MyEventTasksFilter): Promise<EventTask[]> {
  const params = new URLSearchParams();
  if (filters?.subcommitteeId) params.set('subcommitteeId', filters.subcommitteeId);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.category) params.set('category', filters.category);
  if (filters?.eventId) params.set('eventId', filters.eventId);
  const query = params.toString();
  return apiClient<EventTask[]>(`/event-tasks/my-tasks${query ? `?${query}` : ''}`);
}

export function createTask(payload: CreateEventTaskPayload): Promise<EventTask> {
  return apiClient<EventTask>('/event-tasks', {
    method: 'POST',
    body: payload,
  });
}

export function updateTask(id: string, payload: UpdateEventTaskPayload): Promise<EventTask> {
  return apiClient<EventTask>(`/event-tasks/${id}`, {
    method: 'PATCH',
    body: payload,
  });
}

export function deleteTask(id: string): Promise<void> {
  return apiClient<void>(`/event-tasks/${id}`, {
    method: 'DELETE',
  });
}

export function getAllTasks(filters?: { status?: string; category?: string }): Promise<EventTask[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.category) params.set('category', filters.category);
  const query = params.toString();
  return apiClient<EventTask[]>(`/event-tasks${query ? `?${query}` : ''}`);
}

export function getSubcommitteeTasks(
  subcommitteeId: string,
  filters?: { status?: string; category?: string },
): Promise<EventTask[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.category) params.set('category', filters.category);
  const query = params.toString();
  return apiClient<EventTask[]>(
    `/event-tasks/subcommittee/${subcommitteeId}${query ? `?${query}` : ''}`,
  );
}
